import React, { FC, useEffect, useState } from "react"
import { DEF_PG_SIZE_SUB } from "../../constants/maximum-page-size"
import { useLocale } from "../../contexts/localeContext"
import { TableContextProvider } from "../../contexts/table/TableContextProvider"
import { TranslationKeys } from "../../contexts/TranslationKeys"
import presetParameterColumnDescriptor from "../../models/ColumnDefinitions/PresetParameterColumn"
import { ListData } from "../../models/ListData"
import { ListQuery } from "../../models/ListQuery"
import { Lookup } from "../../models/Lookup"
import LookupTypes from "../../models/LookupTypes"
import presetParameterSearchDescriptor from "../../models/search/PresetParameterSearch"
import SiteSearch from "../../models/search/SiteSearch"
import { getPresetParameters } from "../../services/presetParameterService"
import { savePreset } from "../../services/presetService"
import { getSiteNamesWithPagination } from "../../services/siteService"
import { getErrorMessage } from "../../services/utilities/ServiceError"
import { useDialog } from "../dialog/DialogContext"
import { SelectType } from "../settingFields/SettingSelectField"
import TableTab from "../tabs/table-tab"
import { toast } from "react-toastify";
import { PresetSettingsWizard, SystemProfileTypeIds } from "./presetSettingsWizard"
import { getDeviceSettingsTableLegendItems } from "../../models/TableLegend"

interface Props {
    itemId: string;
    onSave: (createNew) => void;
    profileId?: any;
}

const PresetParameters: FC<Props> = ({ itemId, onSave, profileId = SystemProfileTypeIds.Reader, children }) => {
    const { translate } = useLocale();
    const title = translate.byKey(TranslationKeys.assignedParameters);
    const [lookup, setLookup] = useState<Lookup[]>([]);
    const [showModal, setShowModal] = useState(false);
    const [presetParameterId, setPresetParameterId] = useState('');
    const dialog = useDialog();

    useEffect(() => {
        (async () => {
            setLookup([{
                name: LookupTypes.site,
                values: [],
                dataMethod: getSiteNamesWithPagination,
                searchAccessor: SiteSearch.find(item => item.accessor === "name")?.searchOnName
            }]);
        })();
    }, []);

    const handleSave = (formFields, createNew) => {
        savePreset(formFields, createNew)
            .then(success => {
                toast.success(translate.byKey(TranslationKeys.readerPresetSaved));
                onSave(createNew);
                dataRefresh((query) => getPresetParameters(itemId, query));
            })
            .catch(failure => {
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(failure) })
            });
        setPresetParameterId('');
    }

    // default to a method that returns nothing
    // when putting a method in a state variable, useState requires us to return the method from a method, hence the wrapped inner method.
    const [dataRefresh, setDataRefresh] = useState(() => (dataMethod?: (query?: ListQuery) => Promise<ListData>) => { return });

    const manualDataRefresh = (refreshData: (dataMethod?: (query?: ListQuery) => Promise<ListData>) => void) => {
        setDataRefresh(() => refreshData);
    }
    // <-- end context

    return <TableContextProvider
        dataMethod={(listParameters?: ListQuery) => getPresetParameters(itemId, listParameters)}
        manualDataRefresh={manualDataRefresh}
        tableDescriptor={presetParameterColumnDescriptor}
        searchDescriptor={presetParameterSearchDescriptor}
        useTable
        usePagination
        useSearch
        useSort
        useFilter
        useLegend
        legendItems={getDeviceSettingsTableLegendItems(translate)}
        maxPageSize={DEF_PG_SIZE_SUB}
        editMethod={(presetInputId) => { setPresetParameterId(presetInputId); setShowModal(true) }}
        lookup={lookup}>
        <TableTab
            title={title}
            selectRows={false}
            cyName="'presetParameters"
            >
            <PresetSettingsWizard
                isOpen={showModal}
                onClose={() => {
                    setPresetParameterId('');
                    setShowModal(false)
                }}
                title={presetParameterId ? translate.byKey(TranslationKeys.editParameterPreset) : translate.byKey(TranslationKeys.newParameterPreset)}
                presetId={presetParameterId}
                systemProfileId={profileId}
                parentProfileId={itemId}
                presetTemplateType={SelectType.presetParameterTemplate}
                onSave={handleSave}>
            </PresetSettingsWizard>
            {children}
        </TableTab>
    </TableContextProvider>
};

export default PresetParameters;
